import { useCallback, useMemo, useState } from "react"

import { saveAs } from "file-saver"

import { API_MODULE_HORIZONTAL_SIGNING, HorizontalSigningCampaignTable, pdfExportParams } from "@l2r-front/l2r-app-city"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { FileIcon } from "@l2r-front/l2r-icons"
import { Campaign } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext, useWSDispatchContext, useWSStateContext } from "@l2r-front/l2r-query"
import { I18N_NAMESPACE } from "@l2r-front/l2r-shared-campaign"
import { L2RButton, Tabs, useTheme } from "@l2r-front/l2r-ui"

import { useCampaignDispatchContext } from "../../contexts/CampaignContext"
import { useCampaignDetailContext } from "../../contexts/CampaignDetailContext"
import { useCampaignPDFExport } from "../../hooks/mutations/useCampaignPDFExport"
import { useCampaignItemsCount } from "../../hooks/useCampaignItemsCount"
import { useIsCampaignWriter } from "../../hooks/useIsCampaignWriter"

import * as Styled from "./CampaignDetailsTable.styled"

interface CampaignDetailsDataGridProps {
    className?: string
    campaign: Campaign
}

export function CampaignDetailsTable(props: CampaignDetailsDataGridProps) {

    const { className, campaign } = props

    const [tabValue, setTabValue] = useState(0)
    const [{ sortModel, sortHistory }, { setSort }] = useCampaignDetailContext()
    const isCampaignWriter = useIsCampaignWriter(campaign)
    const campaignItemsCount = useCampaignItemsCount(campaign)
    const { t } = useTranslation(I18N_NAMESPACE)
    const theme = useTheme()
    const { launchWebSocket } = useWSDispatchContext()
    const { webSockets } = useWSStateContext()
    const { setSnackbar } = useAlertsDispatchContext()
    const { setIsExportRunning } = useCampaignDispatchContext()

    const pdfExportConfig = useMemo(() => {
        switch (campaign.campaign_type) {
            case API_MODULE_HORIZONTAL_SIGNING:
                return pdfExportParams(t, theme)
        }
    }, [campaign, t, theme])

    const wsId = useMemo(() => {
        return `campaign-pdf-export-${campaign.uuid}`
    }, [campaign])

    const pdfFileName = useMemo(() => {
        return t(I18N_NAMESPACE, "components.campaignDetailsTable.exportPDF.fileName", { campaignName: campaign.name })
    }, [campaign, t])

    const pdfExportMutation = useCampaignPDFExport(pdfExportConfig.url + `?campaign=${campaign.uuid}`,
        {
            errorSnackbarMessage: t(I18N_NAMESPACE, "components.campaignDetailsTable.exportPDF.snackbar.error"),
            onSuccess: (response) => {
                launchWebSocket({
                    id: wsId,
                    url: response.websocket_url,
                    onOpen: () => {
                        console.warn("WebSocket opened for campaign PDF export")
                    },
                    onMessage: (event, webSocket) => {
                        const data = JSON.parse(event.data)
                        if (data.status === "succeeded") {
                            fetch(data.file)
                                .then(response => response.blob())
                                .then(blob => saveAs(blob, pdfFileName))

                            webSocket.close()
                        }
                    },
                    onClose: () => {
                        setIsExportRunning({ [campaign.uuid]: false })
                        console.warn("WebSocket closed for campaign PDF export")
                    },
                    onError: () => {
                        setSnackbar({
                            label: t(I18N_NAMESPACE, "components.campaignDetailsTable.exportPDF.snackbar.error"),
                            severity: "error",
                            anchorOrigin: {
                                horizontal: "left",
                                vertical: "bottom",
                            },
                            duration: 5000,
                        })
                    }
                })
            }
        }
    )

    const handleChangeTab = (_, newValue) => {
        setTabValue(newValue);
    }

    const tabsAttributes = [
        {
            label: "Programme",
            value: 0,
            active: true,
        },
    ]

    const moduleTable = useMemo(() => {
        switch (campaign.campaign_type) {
            case API_MODULE_HORIZONTAL_SIGNING:
                return <HorizontalSigningCampaignTable
                    campaign={campaign}
                    sortModel={sortModel}
                    sortHistory={sortHistory}
                    onSortChange={setSort}
                />
            default:
                return null
        }
    }, [campaign, sortModel, sortHistory, setSort])

    const tabsPanels = useMemo(() => {
        return [
            <Styled.TabPanel
                role="tabpanel"
                hidden={tabValue !== 0}
                id={`campaign-tabpanel-table`}
                key={`campaign-details-table`}
            >
                {tabValue === 0 && moduleTable}
            </Styled.TabPanel>
        ]
    }, [tabValue, moduleTable])

    const handleExportPDF = useCallback(() => {
        setIsExportRunning({ [campaign.uuid]: true })
        pdfExportMutation.mutate({
            ...pdfExportConfig.params,
        })
    }, [campaign, pdfExportMutation, pdfExportConfig, setIsExportRunning])

    const isExportRunning = useMemo(() => {
        const isWebSocketOpen = !!webSockets[wsId]
        return pdfExportMutation.isPending || isWebSocketOpen
    }, [pdfExportMutation, webSockets, wsId])

    return <Styled.Wrapper className={className}>
        <Tabs
            value={tabValue}
            handleChangeTab={handleChangeTab}
            tabsAttributes={tabsAttributes}
        />
        {tabsPanels}
        {(isCampaignWriter && campaignItemsCount > 0) && (
            <Styled.ButtonsContainer>
                <L2RButton
                    variant="secondary"
                    startIcon={<FileIcon extension={"PDF"} />}
                    onClick={handleExportPDF}
                    disabled={isExportRunning}
                    loading={isExportRunning}>
                    {t(I18N_NAMESPACE, "components.campaignDetailsTable.buttons.exportPDF")}
                </L2RButton>
            </Styled.ButtonsContainer>
        )}
    </Styled.Wrapper>
}
